import { useGetGenAIConfiguration } from "@entities/AIControls/lib/hooks/useGetGenAIConfiguration";
import { IAIConfiguration } from "@entities/AIControls/lib/types";
import CollapsibleCard from "@entities/AIControls/ui/CollapsibleCard";
import ControlsCard from "@entities/AIControls/ui/ControlsCard";
import useEditAIConfigurationSettings from "@entities/brief/lib/hooks/useEditAIExecutionSettings";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Tag,
  Typography,
} from "@shared/ui";
import { DisabledPopover } from "@shared/ui/DisabledPopover";
import { Space } from "antd";
import { BrainCircuit, Edit2, Settings2, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";

export default function AutomatedContentReview({
  isDisabled,
}: {
  isDisabled: boolean;
}) {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [checkVsBrief, setCheckVsBrief] = useState<boolean>(true);
  const [checkDraftVsScript, setCheckDraftVsScript] = useState<boolean>(true);
  const [checkVsCampaignGuidelines, setCheckVsCampaignGuidelines] =
    useState<boolean>(true);
  const [checkPerformance, setCheckPerformance] = useState<boolean>(false);

  const [checkVsBrandGuidelines, setCheckVsBrandGuidelines] =
    useState<boolean>(false);
  const [brandGuidelines, setBrandGuidelines] = useState<string>("");
  const [isEditingBrandGuidelines, setIsEditingBrandGuidelines] =
    useState<boolean>(false);

  const [customPromptEnabled, setCustomPromptEnabled] =
    useState<boolean>(false);
  const [customContentPrompt, setCustomContentPrompt] = useState<string>("");
  const [isEditingCustomPrompt, setIsEditingCustomPrompt] =
    useState<boolean>(false);
  const [updatingSwitch, setUpdatingSwitch] = useState<string | null>(null);

  const { briefId } = useBriefStore();

  const { genAIConfigurationData } = useGetGenAIConfiguration(briefId);
  const {
    editAiExecutionSettings,
    isEditAiExecutionSettingsLoading,
    isEditAiExecutionSettingsSuccess,
    editAiExecutionSettingsError
  } = useEditAIConfigurationSettings();
  const handleSave = (payload: Partial<IAIConfiguration>, switchName: string) => {
    setUpdatingSwitch(switchName);
    editAiExecutionSettings({ briefId, ...payload });
  };

  useEffect(() => {
    if (isEditAiExecutionSettingsSuccess) {
      ToastService.success("AI configuration updated successfully");
      if (updatingSwitch === "aiBrandGuardRailsChecks") {
        setIsEditingBrandGuidelines(false);
      } else if (updatingSwitch === "aiAdvancedPromptingChecks") {
        setIsEditingCustomPrompt(false);
      }
      setUpdatingSwitch(null);
    } else if (editAiExecutionSettingsError) {
      ToastService.error(editAiExecutionSettingsError?.message);
      setUpdatingSwitch(null);
    }
  }, [
    editAiExecutionSettingsError,
    isEditAiExecutionSettingsSuccess,
  ]);

  useEffect(() => {
    if (genAIConfigurationData) {
      setIsActive(genAIConfigurationData?.data?.isAiChecksEnabled);
      setCheckDraftVsScript(
        genAIConfigurationData?.data?.isAiScriptVsDraftingConsistencyCheck
      );
      setCheckVsBrief(genAIConfigurationData?.data?.isAiConsistencyCheck);
      setCheckVsBrandGuidelines(
        genAIConfigurationData?.data?.isAiBrandGuardRailsChecks
      );
      setBrandGuidelines(
        genAIConfigurationData?.data?.aiBrandGuardRailsChecks?.[0]
      );
      setCustomPromptEnabled(
        genAIConfigurationData?.data?.isAiAdvancedPromptingChecks
      );
      setCustomContentPrompt(
        genAIConfigurationData?.data?.aiAdvancedPromptingChecks?.[0]
      );
      setCheckPerformance(genAIConfigurationData?.data?.isAiPerformanceChecks);
      setCheckVsCampaignGuidelines(
        genAIConfigurationData?.data?.isAiSafetyChecks
      );
    }
  }, [genAIConfigurationData]);

  return (
    <CollapsibleCard
      isDisabled={isDisabled}
      collapseProps={{
        items: [
          {
            key: "automated-content-review",
            label: (
              <div>
                <div className={styles.headerContent}>
                  <div className={styles.iconWrapper}>
                    <BrainCircuit size={24} />
                  </div>
                  <div>
                    <Typography.Title level={2} className={styles.title}>
                      Automated Content Review
                    </Typography.Title>
                    <Typography.Text className={styles.description}>
                      Review scripts and drafts against guidelines before human
                      approval.
                    </Typography.Text>
                  </div>
                </div>
              </div>
            ),
            children: (
              <Row gutter={[20, 20]}>
                <Col span={12}>
                  <ControlsCard
                    isDisabled={isDisabled}
                    title="Consistency Check"
                    subtitle="Ensures submitted content covers key points from the brief."
                    description={
                      <Tag color={checkVsBrief ? "success" : "default"}>
                        {checkVsBrief ? "Active" : "Inactive"}
                      </Tag>
                    }
                    switchProps={{
                      checked: checkVsBrief,
                      onChange: (checked) => {
                        setCheckVsBrief(checked);
                        handleSave(
                          {
                            isAiConsistencyCheck: checked,
                            isAiChecksEnabled: isActive
                          },
                          "isAiConsistencyCheck"
                        );
                      },
                      loading: updatingSwitch === "isAiConsistencyCheck",
                    }}
                  />
                </Col>
                <Col span={12}>
                  <ControlsCard
                    isDisabled={isDisabled}
                    title="Script vs Draft"
                    subtitle="Verifies that the video content matches the approved script."
                    description={
                      <Tag
                        color={checkDraftVsScript ? "success" : "default"}
                      >
                        {checkDraftVsScript ? "Active" : "Inactive"}
                      </Tag>
                    }
                    switchProps={{
                      checked: checkDraftVsScript,
                      onChange: (checked) => {
                        setCheckDraftVsScript(checked);
                        handleSave(
                          {
                            isAiScriptVsDraftingConsistencyCheck: checked,
                            isAiChecksEnabled: isActive
                          },
                          "isAiScriptVsDraftingConsistencyCheck"
                        );
                      },
                      loading:
                        updatingSwitch ===
                        "isAiScriptVsDraftingConsistencyCheck",
                    }}
                  />
                </Col>
                <Col span={12}>
                  <ControlsCard
                    isDisabled={isDisabled}
                    title="Safety & Guidelines"
                    subtitle="Checks for restricted topics, competitor mentions, and tone."
                    description={
                      <Tag
                        color={
                          checkVsCampaignGuidelines ? "success" : "default"
                        }
                      >
                        {checkVsCampaignGuidelines ? "Active" : "Inactive"}
                      </Tag>
                    }
                    switchProps={{
                      checked: checkVsCampaignGuidelines,
                      onChange: (checked) => {
                        setCheckVsCampaignGuidelines(checked);
                        handleSave(
                          {
                            isAiSafetyChecks: checked,
                            isAiChecksEnabled: isActive
                          },
                          "isAiSafetyChecks"
                        );
                      },
                      loading: updatingSwitch === "isAiSafetyChecks",
                    }}
                  />
                </Col>
                <Col span={12}>
                  <ControlsCard
                    isDisabled={isDisabled}
                    title="Performance Checks"
                    subtitle="Reviews content against identified performance checklist (hooks, retention, CTA)."
                    description={
                      <Tag color={checkPerformance ? "success" : "default"}>
                        {checkPerformance ? "Active" : "Inactive"}
                      </Tag>
                    }
                    switchProps={{
                      checked: checkPerformance,
                      onChange: (checked) => {
                        setCheckPerformance(checked);
                        handleSave(
                          {
                            isAiPerformanceChecks: checked,
                            isAiChecksEnabled: isActive
                          },
                          "isAiPerformanceChecks"
                        );
                      },
                      loading: updatingSwitch === "isAiPerformanceChecks",
                    }}
                  />
                </Col>

                <Col span={12}>
                  <ControlsCard
                    isDisabled={isDisabled}
                    icon={<ShieldCheck color="#635bff" size={16} />}
                    title="Brand Guardrails"
                    subtitle="Global rules for all content (tone, colors, voice)."
                    switchProps={{
                      checked: checkVsBrandGuidelines,
                      onChange: (checked) => {
                        setCheckVsBrandGuidelines(checked);
                        if (!checked) {
                          setBrandGuidelines("");
                          setIsEditingBrandGuidelines(false);
                          handleSave(
                            {
                              isAiBrandGuardRailsChecks: checked,
                              aiBrandGuardRailsChecks: [],
                              isAiChecksEnabled: isActive
                            },
                            "isAiBrandGuardRailsChecks"
                          );
                        } else {
                          handleSave(
                            {
                              isAiBrandGuardRailsChecks: checked,
                              isAiChecksEnabled: isActive
                            },
                            "isAiBrandGuardRailsChecks"
                          );
                        }
                      },
                      loading:
                        updatingSwitch === "isAiBrandGuardRailsChecks",
                    }}
                    description={
                      checkVsBrandGuidelines ? (
                        isEditingBrandGuidelines ? (
                          <Space
                            direction="vertical"
                            style={{ width: "100%" }}
                          >
                            <Input.TextArea
                              value={brandGuidelines}
                              onChange={(e) =>
                                setBrandGuidelines(e.target.value)
                              }
                              placeholder="E.g. 'Tone should be professional but friendly. Never mention politics. Always use the Oxford comma.'"
                              autoSize={{ minRows: 4, maxRows: 8 }}
                              autoFocus
                            />
                            <Row justify="end">
                              <Button
                                type="primary"
                                onClick={() => {
                                  handleSave(
                                    {
                                      aiBrandGuardRailsChecks: [
                                        brandGuidelines,
                                      ],
                                      isAiChecksEnabled: isActive
                                    },
                                    "aiBrandGuardRailsChecks"
                                  );
                                }}
                                loading={
                                  updatingSwitch ===
                                  "aiBrandGuardRailsChecks"
                                }
                              >
                                Save
                              </Button>
                            </Row>
                          </Space>
                        ) : (
                          <div className={styles.instructionCardContainer}>
                            <Card
                              style={{
                                backgroundColor: "#f9fafb",
                                maxHeight: 120,
                                overflowY: "auto",
                                border: "none",
                              }}
                              styles={{
                                body: {
                                  padding: 10,
                                },
                              }}
                            >
                              <Typography.Paragraph>
                                {brandGuidelines || "No instructions set."}
                              </Typography.Paragraph>
                            </Card>
                            <DisabledPopover isDisabled={isDisabled}>
                              <Button
                                className={styles.editButton}
                                disabled={isDisabled}
                                icon={<Edit2 size={10} />}
                                onClick={() =>
                                  setIsEditingBrandGuidelines(true)
                                }
                                size="small"
                              >
                                Edit
                              </Button>
                            </DisabledPopover>
                          </div>
                        )
                      ) : null
                    }
                  />
                </Col>
                <Col span={12}>
                  <ControlsCard
                    isDisabled={isDisabled}
                    icon={<Settings2 color="#635bff" size={16} />}
                    title="Advanced Prompting"
                    subtitle="Inject specific instructions into the analysis model."
                    switchProps={{
                      checked: customPromptEnabled,
                      onChange: (checked) => {
                        setCustomPromptEnabled(checked);
                        if (!checked) {
                          setCustomContentPrompt("");
                          setIsEditingCustomPrompt(false);
                          handleSave(
                            {
                              isAiAdvancedPromptingChecks: checked,
                              aiAdvancedPromptingChecks: [],
                              isAiChecksEnabled: isActive
                            },
                            "isAiAdvancedPromptingChecks"
                          );
                        } else {
                          handleSave(
                            {
                              isAiAdvancedPromptingChecks: checked,
                              isAiChecksEnabled: isActive
                            },
                            "isAiAdvancedPromptingChecks"
                          );
                        }
                      },
                      loading:
                        updatingSwitch === "isAiAdvancedPromptingChecks",
                    }}
                    description={
                      customPromptEnabled ? (
                        isEditingCustomPrompt ? (
                          <Space
                            direction="vertical"
                            style={{ width: "100%" }}
                          >
                            <Input.TextArea
                              value={customContentPrompt}
                              onChange={(e) =>
                                setCustomContentPrompt(e.target.value)
                              }
                              placeholder="E.g. 'Check if the creator smiles in the first 3 seconds. Ensure the logo is visible for at least 2 seconds.'"
                              autoSize={{ minRows: 4, maxRows: 8 }}
                              autoFocus
                            />
                            <Row justify="end">
                              <Button
                                type="primary"
                                onClick={() => {
                                  handleSave(
                                    {
                                      aiAdvancedPromptingChecks: [
                                        customContentPrompt,
                                      ],
                                      isAiChecksEnabled: isActive
                                    },
                                    "aiAdvancedPromptingChecks"
                                  );
                                }}
                                loading={
                                  updatingSwitch ===
                                  "aiAdvancedPromptingChecks"
                                }
                              >
                                Save
                              </Button>
                            </Row>
                          </Space>
                        ) : (
                          <div className={styles.instructionCardContainer}>
                            <Card
                              style={{
                                backgroundColor: "#f9fafb",
                                maxHeight: 120,
                                overflowY: "auto",
                                border: "none",
                              }}
                              styles={{
                                body: {
                                  padding: 10,
                                },
                              }}
                            >
                              <Typography.Paragraph>
                                {customContentPrompt ||
                                  "No instructions set."}
                              </Typography.Paragraph>
                            </Card>
                            <DisabledPopover isDisabled={isDisabled}>
                              <Button
                                disabled={isDisabled}
                                className={styles.editButton}
                                icon={<Edit2 size={10} />}
                                onClick={() =>
                                  setIsEditingCustomPrompt(true)
                                }
                                size="small"
                              >
                                Edit
                              </Button>
                            </DisabledPopover>
                          </div>
                        )
                      ) : null
                    }
                  />
                </Col>
              </Row>
            ),
          },
        ],
        collapsible: "icon",
        activeKey: isActive ? ["automated-content-review"] : [],
        onChange: (key) => {
          if (isDisabled) return;
          if (key?.[0] === "automated-content-review") {
            handleSave({ isAiChecksEnabled: true }, "isAiChecksEnabled");
          } else {
            handleSave({ isAiChecksEnabled: false }, "isAiChecksEnabled");
          }
          setUpdatingSwitch("isAiChecksEnabled");
        },
      }}
      switchProps={{
        loading: updatingSwitch === "isAiChecksEnabled",
      }}
    />
  );
}
